import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import * as FaIcons from 'react-icons/fa';
import { SuccessSwal, ErrorSwal } from '@shared/helpers/swal';

const CreateConductorModal = ({
  show,
  onHide,
  onCreateConductor,
  empresas,
  tiposFlota,
  centrosDespacho,
  sistemasTMS,
  tiposDocumento,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    // Datos básicos del conductor
    nombre: '',
    apellido: '',
    rut: '',
    telefono: '',
    email: '',
    // Datos del usuario móvil
    usuarioSpread: '',
    empresa_id: null,
    tipo_documento_id: null,
    tipo_flota_id: null,
    tms_id: null,
    centro_despacho_id: null
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [usuarioSpreadManuallyEdited, setUsuarioSpreadManuallyEdited] = useState(false);

  // Resetear formulario cuando se abre/cierra el modal
  useEffect(() => {
    if (show) {
      setFormData({
        nombre: '',
        apellido: '',
        rut: '',
        telefono: '',
        email: '',
        usuarioSpread: '',
        empresa_id: null,
        tipo_documento_id: null,
        tipo_flota_id: null,
        tms_id: null,
        centro_despacho_id: null
      });
      setErrors({});
      setUsuarioSpreadManuallyEdited(false);
    }
  }, [show]);

  // Generar UsuarioSpread automáticamente cuando cambian nombre, apellido o empresa
  useEffect(() => {
    // Solo regenerar si no ha sido editado manualmente por el usuario
    if (formData.nombre && formData.apellido && formData.empresa_id && !usuarioSpreadManuallyEdited) {
      const empresa = empresas.find(emp => emp.id === formData.empresa_id);
      if (empresa) {
        const usuarioGenerado = `${empresa.sigla}- ${formData.nombre} ${formData.apellido}`;
        setFormData(prev => ({
          ...prev,
          usuarioSpread: usuarioGenerado
        }));
      }
    }
  }, [formData.nombre, formData.apellido, formData.empresa_id, empresas, usuarioSpreadManuallyEdited]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo cuando se modifica
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validación del RUT chileno
  const validateRUT = (rut) => {
    if (!rut) return false;
    
    // Limpiar el RUT (quitar puntos y guiones)
    const cleanRUT = rut.replace(/[.-]/g, '');
    
    // Verificar que tenga el formato correcto
    if (!/^\d{7,8}[0-9Kk]$/.test(cleanRUT)) {
      return false;
    }
    
    // Extraer cuerpo y dígito verificador
    const cuerpo = cleanRUT.slice(0, -1);
    const dv = cleanRUT.slice(-1).toUpperCase();
    
    // Calcular dígito verificador
    let suma = 0;
    let multiplicador = 2;
    
    for (let i = cuerpo.length - 1; i >= 0; i--) {
      suma += parseInt(cuerpo[i]) * multiplicador;
      multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
    }
    
    const resto = suma % 11;
    const dvCalculado = resto === 0 ? '0' : resto === 1 ? 'K' : (11 - resto).toString();
    
    return dv === dvCalculado;
  };

  // Formatear RUT mientras se escribe
  const formatRUT = (value) => {
    // Limpiar el valor
    const cleaned = value.replace(/[^0-9Kk]/g, '');
    
    if (cleaned.length <= 1) return cleaned;
    
    // Extraer cuerpo y dígito verificador
    const cuerpo = cleaned.slice(0, -1);
    const dv = cleaned.slice(-1);
    
    // Formatear con puntos y guión
    let formatted = '';
    for (let i = 0; i < cuerpo.length; i++) {
      if (i > 0 && (cuerpo.length - i) % 3 === 0) {
        formatted += '.';
      }
      formatted += cuerpo[i];
    }
    
    return formatted + (dv ? '-' + dv.toUpperCase() : '');
  };

  const handleRUTChange = (e) => {
    const formatted = formatRUT(e.target.value);
    handleFieldChange('rut', formatted);
  };

  const handleUsuarioSpreadChange = (e) => {
    const value = e.target.value;
    handleFieldChange('usuarioSpread', value);
    // Marcar como editado manualmente si el usuario escribe algo
    setUsuarioSpreadManuallyEdited(true);
  };

  const validateForm = () => {
    const newErrors = {};

    // Validar campos obligatorios básicos
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es obligatorio';
    if (!formData.apellido.trim()) newErrors.apellido = 'El apellido es obligatorio';
    if (!formData.rut.trim()) {
      newErrors.rut = 'El RUT es obligatorio';
    } else if (!validateRUT(formData.rut)) {
      newErrors.rut = 'El RUT no es válido';
    }
    if (!formData.telefono.trim()) newErrors.telefono = 'El teléfono es obligatorio';
    if (!formData.email.trim()) {
      newErrors.email = 'El email es obligatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    // Validar campos obligatorios del usuario móvil
    if (!formData.empresa_id) newErrors.empresa_id = 'La empresa es obligatoria';
    if (!formData.tipo_documento_id) newErrors.tipo_documento_id = 'El tipo de documento es obligatorio';
    if (!formData.tipo_flota_id) newErrors.tipo_flota_id = 'El tipo de flota es obligatorio';
    if (!formData.tms_id) newErrors.tms_id = 'El TMS es obligatorio';
    if (!formData.centro_despacho_id) newErrors.centro_despacho_id = 'El centro de despacho es obligatorio';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Preparar datos para la API
      const requestData = {
        nombre: formData.nombre.trim(),
        apellido: formData.apellido.trim(),
        rut: formData.rut.trim(),
        telefono: formData.telefono.trim(),
        email: formData.email.trim(),
        tipo_documento_id: formData.tipo_documento_id,
        usuario_movil: {
          empresa_id: formData.empresa_id,
          usuarioSpread: formData.usuarioSpread.trim() || `${empresas.find(e => e.id === formData.empresa_id)?.sigla}- ${formData.nombre} ${formData.apellido}`,
          tipo_flota_id: formData.tipo_flota_id,
          tms_id: formData.tms_id,
          centro_despacho_id: formData.centro_despacho_id
        }
      };

      const result = await onCreateConductor(requestData);
      
      if (result.success) {
        // Mostrar notificación de éxito
        SuccessSwal(
          'Conductor creado exitosamente',
          `Se ha agregado ${formData.nombre} ${formData.apellido} al directorio de conductores.`
        );
        onHide();
      } else {
        // Mostrar notificación de error
        ErrorSwal(
          'Error al crear conductor',
          result.error || 'Ocurrió un error inesperado al crear el conductor.'
        );
      }
    } catch (error) {
      console.error('Error al crear conductor:', error);
      // Mostrar notificación de error para excepciones
      ErrorSwal(
        'Error al crear conductor',
        'Ocurrió un error inesperado. Por favor, intenta nuevamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Preparar opciones para los selects
  const empresaOptions = empresas.map(empresa => ({
    value: empresa.id,
    label: empresa.sigla
  }));

  const tipoDocumentoOptions = tiposDocumento.map(tipo => ({
    value: tipo.id,
    label: tipo.documento
  }));

  const tipoFlotaOptions = tiposFlota.map(tipo => ({
    value: tipo.id,
    label: tipo.tipo_flota
  }));

  const tmsOptions = sistemasTMS.map(tms => ({
    value: tms.id,
    label: tms.tms
  }));

  const centroDespachoOptions = centrosDespacho.map(centro => ({
    value: centro.id,
    label: centro.centro_despacho
  }));

  return (
    <Modal 
      show={show} 
      onHide={isSubmitting ? undefined : onHide} 
      size="lg" 
      centered
      backdrop="static"
    >
      {/* Overlay de loading */}
      {isSubmitting && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1050,
            borderRadius: '0.375rem'
          }}
        >
          <div className="text-center">
            <Spinner animation="border" size="lg" className="text-primary mb-3" />
            <h5 className="text-primary">Creando conductor...</h5>
            <p className="text-muted">Por favor espera mientras procesamos la información</p>
          </div>
        </div>
      )}
      
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>
          <FaIcons.FaUserPlus className="me-2" style={{ color: '#17a2b8' }} />
          Agregar nuevo conductor al Directorio
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Sección 1: Datos Básicos del Conductor */}
          <div className="mb-4">
            <h6 className="text-primary mb-3">
              <FaIcons.FaUser className="me-2" />
              Datos Básicos del Conductor
            </h6>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => handleFieldChange('nombre', e.target.value)}
                    isInvalid={!!errors.nombre}
                    placeholder="Ingrese el nombre"
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.nombre}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Apellido <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.apellido}
                    onChange={(e) => handleFieldChange('apellido', e.target.value)}
                    isInvalid={!!errors.apellido}
                    placeholder="Ingrese el apellido"
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.apellido}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>RUT <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.rut}
                    onChange={handleRUTChange}
                    isInvalid={!!errors.rut}
                    placeholder="12.345.678-9"
                    maxLength={12}
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.rut}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Teléfono <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => handleFieldChange('telefono', e.target.value)}
                    isInvalid={!!errors.telefono}
                    placeholder="+56 9 1234 5678"
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.telefono}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    isInvalid={!!errors.email}
                    placeholder="conductor@ejemplo.com"
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Sección 2: Datos del Usuario Móvil */}
          <div className="mb-3">
            <h6 className="text-primary mb-3">
              <FaIcons.FaMobile className="me-2" />
              Datos del Usuario Móvil
            </h6>
            
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Usuario Spread</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.usuarioSpread}
                    onChange={handleUsuarioSpreadChange}
                    placeholder="Se completará automáticamente si se deja vacío"
                    disabled={isSubmitting}
                  />
                  <Form.Text className="text-muted">
                    Se genera automáticamente con el formato "SIGLA- Nombre Apellido". Si editas manualmente, dejará de auto-generarse.
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Empresa <span className="text-danger">*</span></Form.Label>
                  <Select
                    value={empresaOptions.find(opt => opt.value === formData.empresa_id) || null}
                    onChange={(option) => handleFieldChange('empresa_id', option?.value || null)}
                    options={empresaOptions}
                    placeholder="Seleccionar empresa..."
                    isClearable
                    isSearchable
                    isDisabled={isSubmitting}
                  />
                  {errors.empresa_id && (
                    <div className="text-danger small mt-1">{errors.empresa_id}</div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Tipo de Documento <span className="text-danger">*</span></Form.Label>
                  <Select
                    value={tipoDocumentoOptions.find(opt => opt.value === formData.tipo_documento_id) || null}
                    onChange={(option) => handleFieldChange('tipo_documento_id', option?.value || null)}
                    options={tipoDocumentoOptions}
                    placeholder="Seleccionar tipo..."
                    isClearable
                    isSearchable
                    isDisabled={isSubmitting}
                  />
                  {errors.tipo_documento_id && (
                    <div className="text-danger small mt-1">{errors.tipo_documento_id}</div>
                  )}
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Tipo Flota <span className="text-danger">*</span></Form.Label>
                  <Select
                    value={tipoFlotaOptions.find(opt => opt.value === formData.tipo_flota_id) || null}
                    onChange={(option) => handleFieldChange('tipo_flota_id', option?.value || null)}
                    options={tipoFlotaOptions}
                    placeholder="Seleccionar tipo..."
                    isClearable
                    isSearchable
                    isDisabled={isSubmitting}
                  />
                  {errors.tipo_flota_id && (
                    <div className="text-danger small mt-1">{errors.tipo_flota_id}</div>
                  )}
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>TMS <span className="text-danger">*</span></Form.Label>
                  <Select
                    value={tmsOptions.find(opt => opt.value === formData.tms_id) || null}
                    onChange={(option) => handleFieldChange('tms_id', option?.value || null)}
                    options={tmsOptions}
                    placeholder="Seleccionar TMS..."
                    isClearable
                    isSearchable
                    isDisabled={isSubmitting}
                  />
                  {errors.tms_id && (
                    <div className="text-danger small mt-1">{errors.tms_id}</div>
                  )}
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Centro Despacho <span className="text-danger">*</span></Form.Label>
                  <Select
                    value={centroDespachoOptions.find(opt => opt.value === formData.centro_despacho_id) || null}
                    onChange={(option) => handleFieldChange('centro_despacho_id', option?.value || null)}
                    options={centroDespachoOptions}
                    placeholder="Seleccionar centro..."
                    isClearable
                    isSearchable
                    isDisabled={isSubmitting}
                  />
                  {errors.centro_despacho_id && (
                    <div className="text-danger small mt-1">{errors.centro_despacho_id}</div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={onHide}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Guardando conductor...
              </>
            ) : (
              <>
                <FaIcons.FaSave className="me-2" />
                Guardar Conductor
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

CreateConductorModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onCreateConductor: PropTypes.func.isRequired,
  empresas: PropTypes.array.isRequired,
  tiposFlota: PropTypes.array.isRequired,
  centrosDespacho: PropTypes.array.isRequired,
  sistemasTMS: PropTypes.array.isRequired,
  tiposDocumento: PropTypes.array.isRequired,
  loading: PropTypes.bool
};

export default CreateConductorModal;
